import { HTTP_METHODS } from '../constants/httpMethods';
import { apiService } from './ApiService';
import { ApiResponse } from '../interfaces/responses/ApiResponse';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { IAccessoryCategory, IAccessoryFamily, IAccessoryType } from '../interfaces/heavyDutyMachinery/IAccessoryTaxonomy';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

// See TaxonomyService's own doc comment -- same region-qualified-language
// narrowing, same reasoning.
const normalizeLanguage = (language: string): string => (language || 'es').split('-')[0].toLowerCase();

// Same "one in-memory promise per language, shared across every mounted
// consumer" pattern as TaxonomyService's own taxonomyPromises -- see that
// file's doc comment.
const accessoryTaxonomyPromises = new Map<string, Promise<IAccessoryCategory[]>>();

export const getAccessoryTaxonomy = async (language: string): Promise<IAccessoryCategory[]> => {
  const lang = normalizeLanguage(language);
  let promise = accessoryTaxonomyPromises.get(lang);
  if (!promise) {
    promise = (async () => {
      const response = await apiService<ApiResponse<IAccessoryCategory[]>>({
        url: API_ENDPOINTS.ACCESSORY_TAXONOMY,
        method: HTTP_METHODS.GET,
        params: { lang },
        baseUrl: heavyDutyMachineryBaseUrl,
      });
      if (response.success) return response.data.data;
      throw new Error(response.error || 'Failed to load accessory taxonomy');
    })();
    promise.catch(() => { accessoryTaxonomyPromises.delete(lang); });
    accessoryTaxonomyPromises.set(lang, promise);
  }
  return promise;
};

/** Every leaf IAccessoryType across every category/family, flattened. */
export const flattenAccessoryTypes = (categories: IAccessoryCategory[]): IAccessoryType[] =>
  categories.flatMap((category) => category.families.flatMap((family) => family.types));

/** IAccessoryFamily -> its parent IAccessoryCategory, for consumers that only have a family and need to group/label by category too. */
export const flattenAccessoryFamilies = (categories: IAccessoryCategory[]): { category: IAccessoryCategory; family: IAccessoryFamily }[] =>
  categories.flatMap((category) => category.families.map((family) => ({ category, family })));

/** Looks up a single AccessoryType by MachineryAccessory.AccessoryTypeCode (AccessoryType.Code -- language-neutral, see IAccessoryTaxonomy.ts's own doc comment). */
export const findAccessoryTypeByCode = (categories: IAccessoryCategory[], code: string): IAccessoryType | undefined =>
  flattenAccessoryTypes(categories).find((type) => type.code === code);
